import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, ChevronDown, X, Check } from 'lucide-react';

export interface MediaTypeOption {
  id: string;
  label: string;
  icon: React.ElementType | null;
}

interface MediaTypeDropdownProps {
  options: MediaTypeOption[];
  selectedTypeId: string;
  onTypeSelect: (typeId: string) => void;
  className?: string;
}

export const MediaTypeDropdown: React.FC<MediaTypeDropdownProps> = ({
  options,
  selectedTypeId,
  onTypeSelect,
  className = ''
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(option => option.id === selectedTypeId) || options[0];

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        className="flex items-center justify-between w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        onClick={toggleDropdown}
      >
        <div className="flex items-center">
          <Filter className="w-4 h-4 mr-2 text-gray-500" />
          <span className="text-sm mr-1">
            {t('mediaGallery.filters.type')}:
          </span>
          <span className="text-sm font-medium">
            {t(selectedOption.label)}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform duration-150 ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {options.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.id}
                className={`flex items-center w-full px-3 py-2 text-sm hover:bg-gray-50 ${selectedTypeId === option.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                onClick={() => {
                  onTypeSelect(option.id);
                  setIsOpen(false);
                }}
              >
                {Icon && <Icon className="w-4 h-4 mr-2" />}
                {selectedTypeId === option.id && (
                  <Check className="w-4 h-4 mr-1 text-blue-600" />
                )}
                <span>{t(option.label)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}; 